/**
 * Contrato backend — reportes liquidación resumen y detallado.
 *
 * GET query:
 * IdPeriodo?, Estado?, IdProv?, Tipo (0=Tienda/gestor, 1=Proveedor),
 * Pagina, RegistrosPorPagina, idTienda?
 *
 * Valor 0 en UI = Todos → no se envía el parámetro.
 */

import {
  LIQUIDACION_REPORTES_TIPO_TIENDA,
  LIQUIDACION_REPORTES_TIPO_PROVEEDOR,
  RESUMEN_DEFAULT_PER_PAGE,
} from "./liquidacion-reportes.constants";
import { isProveedor } from "@/constants/perfiles";

export const API_SUCCESS_STATUS = 200;

export function getDefaultFiltroLiquidacion() {
  return {
    idPeriodo: 0,
    idTienda: 0,
    idProv: 0,
    estado: 0,
  };
}

export function normalizeFiltroLiquidacionFilters(filters = {}) {
  const idPeriodo = Number(filters.idPeriodo ?? 0);
  const idTienda = Number(filters.idTienda ?? filters.codTienda ?? 0);
  const idProv = Number(filters.idProv ?? 0);
  const estado = Number(filters.estado ?? 0);

  return {
    idPeriodo: Number.isFinite(idPeriodo) && idPeriodo > 0 ? idPeriodo : 0,
    idTienda: Number.isFinite(idTienda) ? idTienda : 0,
    idProv: Number.isFinite(idProv) ? idProv : 0,
    estado: Number.isFinite(estado) ? estado : 0,
  };
}

export function resolveLiquidacionReportTipo(perfil) {
  return isProveedor(perfil)
    ? LIQUIDACION_REPORTES_TIPO_PROVEEDOR
    : LIQUIDACION_REPORTES_TIPO_TIENDA;
}

export function buildReportQueryParams(filters = {}, pagination = {}, options = {}) {
  const normalized = normalizeFiltroLiquidacionFilters(filters);
  const page = Number(pagination.page ?? 1);
  const limit = Number(
    pagination.limit ?? pagination.perPage ?? RESUMEN_DEFAULT_PER_PAGE,
  );
  const tipo = Number(options.tipo ?? LIQUIDACION_REPORTES_TIPO_TIENDA);

  const params = {
    Tipo: tipo === LIQUIDACION_REPORTES_TIPO_PROVEEDOR
      ? LIQUIDACION_REPORTES_TIPO_PROVEEDOR
      : LIQUIDACION_REPORTES_TIPO_TIENDA,
    Pagina: Number.isFinite(page) && page > 0 ? page : 1,
    RegistrosPorPagina: Number.isFinite(limit) && limit > 0 ? limit : RESUMEN_DEFAULT_PER_PAGE,
  };

  if (normalized.idPeriodo > 0) {
    params.IdPeriodo = normalized.idPeriodo;
  }

  if (normalized.estado > 0) {
    params.Estado = normalized.estado;
  }

  const idProv = Number(options.idProv ?? normalized.idProv ?? 0);
  if (idProv > 0) {
    params.IdProv = idProv;
  }

  if (normalized.idTienda > 0) {
    params.idTienda = normalized.idTienda;
  }

  return params;
}

/** Body POST export reporte detallado (camelCase). */
export function buildReportExportBody(filters = {}, options = {}) {
  const normalized = normalizeFiltroLiquidacionFilters(filters);
  const tipo = Number(options.tipo ?? LIQUIDACION_REPORTES_TIPO_TIENDA);
  const idProv = Number(options.idProv ?? normalized.idProv ?? 0);

  return {
    idPeriodo: normalized.idPeriodo,
    estado: normalized.estado,
    idProv: Number.isFinite(idProv) ? idProv : 0,
    idTienda: normalized.idTienda,
    tipo:
      tipo === LIQUIDACION_REPORTES_TIPO_PROVEEDOR
        ? LIQUIDACION_REPORTES_TIPO_PROVEEDOR
        : LIQUIDACION_REPORTES_TIPO_TIENDA,
  };
}

/**
 * Extrae el array de filas del envelope API.
 */
const DATA_ARRAY_KEYS = [
  "registros",
  "items",
  "lista",
  "rows",
  "datos",
  "results",
  "data",
];

function extractArrayFromObject(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  for (const key of DATA_ARRAY_KEYS) {
    if (Array.isArray(value[key])) {
      return value[key];
    }
  }

  return null;
}

export function parseApiEnvelope(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.datos)) {
    return response.data.datos;
  }

  const nestedInData = extractArrayFromObject(response?.data);
  if (nestedInData) {
    return nestedInData;
  }

  const nestedInRoot = extractArrayFromObject(response);
  if (nestedInRoot) {
    return nestedInRoot;
  }

  if (Array.isArray(response?.value)) {
    return response.value;
  }

  return [];
}

export function parseApiTotal(response, fallback = 0) {
  const candidates = [
    response?.totalRegistros,
    response?.total,
    response?.totalRecords,
    response?.totalRows,
    response?.count,
    response?.data?.totalRegistros,
    response?.data?.total,
    response?.data?.totalRecords,
    response?.paginacion?.totalRegistros,
    response?.pagination?.total,
  ];

  for (const value of candidates) {
    const total = Number(value);
    if (Number.isFinite(total) && total >= 0) {
      return total;
    }
  }

  return fallback;
}

/**
 * Total para paginador servidor cuando el API no envía total explícito.
 */
export function resolvePagedTotal(response, items = [], pagination = {}) {
  const explicitTotal = parseApiTotal(response, -1);
  if (explicitTotal >= 0) {
    return explicitTotal;
  }

  const page = Number(pagination.page ?? 1);
  const limit = Number(pagination.limit ?? pagination.perPage ?? 10);
  const safePage = Number.isFinite(page) && page > 0 ? page : 1;
  const safeLimit = Number.isFinite(limit) && limit > 0 ? limit : 10;

  if (items.length < safeLimit) {
    return (safePage - 1) * safeLimit + items.length;
  }

  return safePage * safeLimit + 1;
}

export function parseApiErrorMessage(error, fallback = "Ocurrió un error al cargar el reporte.") {
  if (error?.message?.startsWith("Falta la variable de entorno")) {
    return error.message;
  }

  if (error?.code === "ECONNABORTED") {
    return "No se pudo cargar el reporte. Verifique su conexión e intente nuevamente.";
  }

  const { data, status } = error?.response ?? {};

  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }

  if (data?.message) {
    return data.message;
  }

  if (status === 400) {
    return fallback;
  }

  return fallback;
}
